import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { ancre } from '../tutoriel/ancres';
import { lirePaquet, deciderInteressant, deciderPasInteressant, reporterProposition, annulerProposition } from '../services/propositions';
import AvatarCandidat from './AvatarCandidat';

/**
 * PROFILS PROPOSÉS — le tri, à la façon d'un jeu de cartes.
 *
 * Décision du 22.09 : l'écran Sourceur n'est ouvert qu'à l'administrateur.
 * Pour partager les profils d'une campagne sans inonder la recruteuse, on
 * lui en PROPOSE un paquet (vingt au plus, une note) ; elle fait le filtre.
 * « Intéressant » : le profil entre dans le besoin (le serveur crée ou
 * retrouve la fiche, positionne, joint le CV), elle le qualifie ensuite
 * comme d'habitude. « Pas intéressant » : un motif en un clic, il n'entre
 * pas. Rien n'entre dans le besoin sans son oui.
 *
 * PLAN DE DESIGN (écran nouveau)
 * - L'unité n'est pas une liste, c'est UNE DÉCISION. Une seule carte au
 *   centre d'une colonne étroite ; ce qu'on lit en cinq secondes y est, rien
 *   d'autre (pas de CV, pas de fil : ils seront sur la fiche après le oui).
 * - Hiérarchie : le score en mono, grand, à gauche du nom — c'est la
 *   première question (« pourquoi on me le propose ») ; puis poste,
 *   employeur, lieu ; puis ce qui fait trancher : forts en face des faibles.
 *   La note de celui qui propose est citée à part : c'est la voix d'un
 *   collègue, pas celle du moteur.
 * - Élément signature : la TRANCHE DE DÉCISION. En glissant (ou au clavier),
 *   la carte penche, et un filet vertical se remplit sur le bord vers lequel
 *   elle part — vert succès à droite « Intéressant », encre à gauche « Pas
 *   intéressant ». On voit la conséquence avant de lâcher. Derrière la
 *   carte, deux tranches de cartes au plus : l'épaisseur du paquet est
 *   l'information « il en reste ».
 * - Le VISAGE (24.09) : une vignette carrée de 44 px sous le score, pas une
 *   photo plein cadre — on trie des gens, et une carte sans visage se lit
 *   comme une ligne de tableur. Quand la fiche n'a pas de photo (le cas le
 *   plus fréquent tant que le moteur n'en envoie pas), ce sont les initiales :
 *   même repli que partout, jamais une silhouette générique.
 * - Relu contre le défaut « appli de rencontre » : pas de cœur ni de croix
 *   géante, pas de dégradé rose et vert, pas de photo plein cadre. Trois
 *   boutons textuels sous la carte, le corail de l'application sur
 *   « Intéressant » seulement (le geste qui fait entrer quelqu'un). Bordures
 *   1 px, aucune ombre. Mouvement : 160 ms sur la sortie de carte, rien
 *   sous « réduire les animations ».
 * - Clavier : → intéressant, ← pas intéressant puis 1–5 pour le motif,
 *   ↑ plus tard, Z annule le dernier geste, Échap ferme les motifs.
 */
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} aria-hidden="true" />;
const SEUIL_X = 90, SEUIL_Y = 80;
const fmtJour = (v) => { if (!v) return ''; const d = new Date(v); return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('fr-CH', { day: '2-digit', month: '2-digit' }); };
// Les initiales se calculent sur prénom + nom ; la carte, elle, porte le nom
// complet du moteur (« Javier Sánchez ») et parfois le prénom à part.
const nomEnDeux = (c) => {
  const complet = String(c?.name || '').trim();
  const mots = complet.split(/\s+/).filter(Boolean);
  const prenom = c?.firstName || mots[0] || '';
  const nom = mots.length > 1 ? mots[mots.length - 1] : '';
  return { prenom, nom };
};
const lectureReduite = () => { try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; } };

export default function ProfilsProposes({ showNotification, onOuvrirBesoin, onOuvrirCandidat, besoinInitial = null }) {
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [besoinId, setBesoinId] = useState(besoinInitial ? Number(besoinInitial) : null);
  const [busy, setBusy] = useState(false);
  const [motifsOuverts, setMotifsOuverts] = useState(false);
  const [drag, setDrag] = useState({ dx: 0, dy: 0, actif: false });
  const [sortie, setSortie] = useState(null); // 'droite' | 'gauche' | 'haut' — l'animation de sortie
  const [historique, setHistorique] = useState([]); // ids décidés dans cette session (pour « annuler »)
  const depart = useRef(null);

  const charger = useCallback(async () => {
    const r = await lirePaquet();
    if (!r.ok) { setErreur(r.absente ? 'arrive' : r.error); setData(null); return; }
    setErreur(null); setData(r);
  }, []);
  useEffect(() => { charger(); }, [charger]);

  const paquets = useMemo(() => data?.paquets || [], [data]);
  // Le paquet affiché : celui qu'on a choisi, sinon le premier qui a des cartes.
  const courant = useMemo(() => paquets.find(p => Number(p.besoin.id) === Number(besoinId))
    || paquets.find(p => p.a_trier > 0) || paquets[0] || null, [paquets, besoinId]);
  const carte = courant?.cartes?.[0] || null;
  const restantes = courant?.cartes?.length || 0;
  const motifs = useMemo(() => data?.motifs || [], [data]);

  const apres = useCallback(async (r, id) => {
    if (!r.ok) { showNotification?.(r.error, 'error'); setSortie(null); setDrag({ dx: 0, dy: 0, actif: false }); return; }
    showNotification?.(r.message, 'success');
    if (id != null) setHistorique(h => [...h, id].slice(-20));
    setMotifsOuverts(false);
    await charger();
    setSortie(null); setDrag({ dx: 0, dy: 0, actif: false });
  }, [charger, showNotification]);

  const sortir = (direction) => new Promise(res => { if (lectureReduite()) { res(); return; } setSortie(direction); setTimeout(res, 160); });

  const interessant = useCallback(async () => {
    if (!carte || busy) return;
    setBusy(true);
    await sortir('droite');
    const r = await deciderInteressant(carte.id);
    await apres(r, carte.id);
    setBusy(false);
  }, [carte, busy, apres]);
  const pasInteressant = useCallback(async (motif) => {
    if (!carte || busy) return;
    setBusy(true);
    await sortir('gauche');
    const r = await deciderPasInteressant(carte.id, motif);
    await apres(r, carte.id);
    setBusy(false);
  }, [carte, busy, apres]);
  const plusTard = useCallback(async () => {
    if (!carte || busy) return;
    if (restantes < 2) { showNotification?.('C’est la dernière carte de ce paquet : rien à remettre derrière.', 'info'); setDrag({ dx: 0, dy: 0, actif: false }); return; }
    setBusy(true);
    await sortir('haut');
    const r = await reporterProposition(carte.id);
    await apres(r, null);
    setBusy(false);
  }, [carte, busy, restantes, apres, showNotification]);
  const annulerDernier = useCallback(async (id = null) => {
    const cible = id ?? historique[historique.length - 1];
    if (cible == null || busy) return;
    setBusy(true);
    const r = await annulerProposition(cible);
    showNotification?.(r.ok ? r.message : r.error, r.ok ? 'success' : 'error');
    if (r.ok) { setHistorique(h => h.filter(x => x !== cible)); await charger(); }
    setBusy(false);
  }, [historique, busy, charger, showNotification]);

  // Clavier — uniquement quand aucun champ n'a le focus.
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable || e.metaKey || e.ctrlKey || e.altKey) return;
      if (motifsOuverts) {
        const n = parseInt(e.key, 10);
        if (n >= 1 && n <= motifs.length) { e.preventDefault(); pasInteressant(motifs[n - 1].cle); return; }
        if (e.key === 'Escape') { e.preventDefault(); setMotifsOuverts(false); setDrag({ dx: 0, dy: 0, actif: false }); }
        return;
      }
      if (!carte) return;
      if (e.key === 'ArrowRight') { e.preventDefault(); interessant(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); setMotifsOuverts(true); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); plusTard(); }
      else if (e.key === 'z' || e.key === 'Z') { e.preventDefault(); annulerDernier(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [carte, motifsOuverts, motifs, interessant, pasInteressant, plusTard, annulerDernier]);

  // Glisser (souris, doigt, stylet) : même seuil partout.
  const onDown = (e) => {
    if (busy || motifsOuverts || e.button > 0 || (e.target.closest && e.target.closest('a,button'))) return;
    depart.current = { x: e.clientX, y: e.clientY };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* rien */ }
    setDrag({ dx: 0, dy: 0, actif: true });
  };
  const onMove = (e) => { if (!depart.current) return; setDrag({ dx: e.clientX - depart.current.x, dy: e.clientY - depart.current.y, actif: true }); };
  const onUp = () => {
    if (!depart.current) return;
    depart.current = null;
    const { dx, dy } = drag;
    if (dx > SEUIL_X) interessant();
    else if (dx < -SEUIL_X) { setMotifsOuverts(true); setDrag({ dx: -SEUIL_X, dy: 0, actif: false }); }
    else if (dy < -SEUIL_Y && Math.abs(dx) < SEUIL_X) plusTard();
    else setDrag({ dx: 0, dy: 0, actif: false });
  };

  const dx = sortie === 'droite' ? 520 : sortie === 'gauche' ? -520 : drag.dx;
  const dy = sortie === 'haut' ? -420 : (drag.actif ? drag.dy * 0.35 : 0);
  const penche = Math.max(-1, Math.min(1, dx / 160));
  const transform = `translate(${dx}px, ${dy}px) rotate(${penche * 6}deg)`;

  const c = carte?.carte || {};
  const puces = [
    c.interne ? { t: 'Notre base', i: 'people-fill' } : null,
    c.open_to_work === true ? { t: 'Open to work', i: 'briefcase-fill', k: 'ok' } : null,
    c.disponibilite ? { t: `Dispo : ${c.disponibilite}`, i: 'calendar-check', k: 'ok' } : null,
    c.alerte_langue ? { t: c.alerte_langue.inconnues ? 'Langues non déclarées' : `Langue : ${(c.alerte_langue.manque || []).join(', ')} absent`, i: 'translate', k: 'warn' } : null,
    c.jackpot ? { t: `Jackpot : ${c.jackpot.join(', ')}`, i: 'gem' } : null,
    c.entreprise_visee ? { t: `Vient de ${c.entreprise_visee.join(', ')}`, i: 'building' } : null,
    c.manque ? { t: `Sans ${c.manque.join(', ')}`, i: 'dash-circle', k: 'warn' } : null,
    c.a_completer ? { t: 'Profil peu renseigné', i: 'eye-slash', k: 'warn' } : null,
    c.sous_seuil ? { t: 'Sous le seuil du moteur', i: 'arrow-down-short' } : null,
  ].filter(Boolean);

  return (
    <div className="pp" {...ancre('propositions-paquet')}>
      <style>{`
        .pp { padding: 22px 16px 40px; max-width: 1060px; margin: 0 auto; font-family: var(--erp-font-sans); color: var(--erp-ink); }
        .pp-tete { display: flex; align-items: baseline; gap: 12px; flex-wrap: wrap; margin-bottom: 4px; }
        .pp-titre { font-size: 26px; font-weight: 800; letter-spacing: -0.02em; margin: 0; color: var(--erp-ink-strong); }
        .pp-compte { font-family: var(--erp-font-mono); font-size: 12px; color: var(--erp-text-secondary); }
        .pp-intro { margin: 0 0 16px; font-size: 13px; line-height: 1.55; color: var(--erp-text-secondary); max-width: 64ch; }
        .pp-besoins { display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 18px; }
        .pp-besoin { display: inline-flex; align-items: center; gap: 6px; font: inherit; font-size: 12px; padding: 5px 10px; border-radius: var(--erp-radius-sm); border: 1px solid var(--erp-line); background: var(--erp-surface); color: var(--erp-ink); cursor: pointer; }
        .pp-besoin[aria-pressed="true"] { border-color: var(--erp-ink-strong); color: var(--erp-ink-strong); font-weight: 700; }
        .pp-besoin:focus-visible, .pp-btn:focus-visible { outline: 2px solid var(--erp-focus); outline-offset: 2px; }
        .pp-besoin .n { font-family: var(--erp-font-mono); font-size: 11px; color: var(--erp-text-tertiary); }
        .pp-scene { display: grid; grid-template-columns: minmax(0, 520px) minmax(220px, 1fr); gap: 28px; align-items: start; }
        @media (max-width: 860px) { .pp-scene { grid-template-columns: 1fr; } }
        .pp-pile { position: relative; padding-top: 12px; }
        .pp-tranche { position: absolute; left: 12px; right: 12px; height: 20px; border: 1px solid var(--erp-line); border-bottom: none; border-radius: var(--erp-radius-lg) var(--erp-radius-lg) 0 0; background: var(--erp-surface-muted); }
        .pp-carte { position: relative; background: var(--erp-surface); border: 1px solid var(--erp-line-strong); border-radius: var(--erp-radius-lg); padding: 18px 18px 16px; touch-action: none; user-select: none; cursor: grab; will-change: transform; overflow: hidden; }
        .pp-carte.glisse { cursor: grabbing; }
        .pp-carte.libre { transition: transform 160ms ease; }
        @media (prefers-reduced-motion: reduce) { .pp-carte, .pp-carte.libre { transition: none; } }
        .pp-bord { position: absolute; top: 0; bottom: 0; width: 5px; pointer-events: none; }
        .pp-bord--oui { right: 0; background: var(--erp-success); }
        .pp-bord--non { left: 0; background: var(--erp-ink-strong); }
        .pp-sceau { position: absolute; top: 14px; font-size: 11px; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; padding: 3px 8px; border-radius: var(--erp-radius-xs); pointer-events: none; }
        .pp-sceau--oui { right: 16px; color: var(--erp-success); border: 1px solid var(--erp-success); background: var(--erp-surface); }
        .pp-sceau--non { left: 16px; color: var(--erp-ink-strong); border: 1px solid var(--erp-ink-strong); background: var(--erp-surface); }
        .pp-haut { display: flex; gap: 14px; align-items: flex-start; }
        .pp-score { font-family: var(--erp-font-mono); font-size: 30px; font-weight: 700; line-height: 1; color: var(--erp-ink-strong); min-width: 44px; }
        .pp-tete-g { display: flex; flex-direction: column; align-items: center; gap: 10px; }
        .pp-visage { border: 1px solid var(--erp-line); }
        .pp-score small { display: block; font-size: 10px; font-weight: 400; color: var(--erp-text-tertiary); margin-top: 4px; letter-spacing: 0.04em; }
        .pp-nom { font-size: 18px; font-weight: 800; letter-spacing: -0.01em; color: var(--erp-ink-strong); margin: 0; }
        .pp-poste { font-size: 13px; color: var(--erp-ink); margin-top: 3px; }
        .pp-lieu { font-size: 12px; color: var(--erp-text-secondary); margin-top: 2px; }
        .pp-puces { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 12px; }
        .pp-puce { display: inline-flex; align-items: center; gap: 5px; font-size: 10.5px; font-weight: 700; padding: 2px 7px; border-radius: var(--erp-radius-xs); border: 1px solid var(--erp-line); color: var(--erp-text-secondary); }
        .pp-puce.ok { border-color: var(--erp-success); color: var(--erp-success); }
        .pp-puce.warn { border-color: var(--erp-warning); color: var(--erp-warning); }
        .pp-cols { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-top: 14px; }
        @media (max-width: 480px) { .pp-cols { grid-template-columns: 1fr; } }
        .pp-lbl { font-size: 10.5px; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase; color: var(--erp-text-tertiary); margin-bottom: 5px; }
        .pp-li { display: flex; gap: 6px; font-size: 12.5px; line-height: 1.4; color: var(--erp-ink); margin-bottom: 4px; }
        .pp-raisons { margin-top: 12px; font-size: 12px; line-height: 1.5; color: var(--erp-text-secondary); display: -webkit-box; -webkit-line-clamp: 4; -webkit-box-orient: vertical; overflow: hidden; }
        .pp-note { margin-top: 12px; padding: 8px 10px; border-left: 3px solid var(--erp-accent); background: var(--erp-accent-soft); font-size: 12.5px; line-height: 1.45; color: var(--erp-ink); border-radius: 0 var(--erp-radius-xs) var(--erp-radius-xs) 0; }
        .pp-note b { font-weight: 700; }
        .pp-pied { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-top: 14px; font-size: 11.5px; color: var(--erp-text-tertiary); }
        .pp-pied a { color: var(--erp-text-secondary); }
        .pp-motifs { position: absolute; left: 0; right: 0; bottom: 0; background: var(--erp-surface); border-top: 1px solid var(--erp-line-strong); padding: 12px 14px 14px; }
        .pp-motifs-t { font-size: 12.5px; font-weight: 700; color: var(--erp-ink-strong); margin-bottom: 8px; display: flex; justify-content: space-between; }
        .pp-motifs-l { display: flex; flex-wrap: wrap; gap: 6px; }
        .pp-actions { display: grid; grid-template-columns: 1fr auto 1fr; gap: 8px; margin-top: 14px; }
        .pp-kbd { font-family: var(--erp-font-mono); font-size: 10px; padding: 0 4px; border: 1px solid var(--erp-line); border-radius: 3px; margin-left: 6px; color: var(--erp-text-tertiary); }
        .pp-annuler { margin-top: 10px; font-size: 12px; }
        .pp-lien { border: none; background: none; padding: 0; font: inherit; color: var(--erp-text-secondary); text-decoration: underline; cursor: pointer; }
        .pp-lien:disabled { opacity: .5; cursor: default; }
        .pp-cote { font-size: 12.5px; color: var(--erp-text-secondary); }
        .pp-cote h3 { font-size: 11px; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase; color: var(--erp-text-tertiary); margin: 0 0 8px; }
        .pp-fait { display: flex; gap: 8px; align-items: baseline; padding: 6px 0; border-top: 1px solid var(--erp-line); font-size: 12.5px; }
        .pp-fait:first-of-type { border-top: none; }
        .pp-vide { border: 1px solid var(--erp-line); border-radius: var(--erp-radius-lg); padding: 22px 18px; font-size: 13px; line-height: 1.55; color: var(--erp-text-secondary); background: var(--erp-surface); }
      `}</style>

      <div className="pp-tete">
        <h1 className="pp-titre">Profils proposés</h1>
        {data && <span className="pp-compte">{data.total} à trier{courant ? ` · ${courant.faits.interessant || 0} intéressant${(courant.faits.interessant || 0) > 1 ? 's' : ''} · ${courant.faits.pas_interessant || 0} non` : ''}</span>}
      </div>
      <p className="pp-intro">Les profils trouvés par une campagne de sourcing et qu’on vous propose. <b>Intéressant</b> : il entre dans le besoin, vous le qualifiez ensuite comme d’habitude. <b>Pas intéressant</b> : dites pourquoi en un clic, il n’entre pas. Rien n’entre dans le besoin sans votre oui.</p>

      {erreur === 'arrive' && <div className="pp-vide">Les profils proposés arrivent côté serveur.</div>}
      {erreur && erreur !== 'arrive' && <div className="pp-vide" style={{ color: 'var(--erp-danger)', borderColor: 'var(--erp-danger)' }}>{erreur}</div>}
      {!data && !erreur && <div className="pp-vide"><BI icon="arrow-repeat" style={{ display: 'inline-block', animation: 'ats-spin .8s linear infinite' }} /> Chargement…</div>}

      {data && paquets.length > 1 && (
        <div className="pp-besoins" role="group" aria-label="Besoins">
          {paquets.map(p => (
            <button key={p.besoin.id} type="button" className="pp-besoin" aria-pressed={courant && Number(courant.besoin.id) === Number(p.besoin.id)}
              onClick={() => { setBesoinId(p.besoin.id); setMotifsOuverts(false); setDrag({ dx: 0, dy: 0, actif: false }); }}>
              {p.besoin.titre}<span className="n">{p.a_trier}</span>
            </button>
          ))}
        </div>
      )}

      {data && !paquets.length && (
        <div className="pp-vide">Rien à trier. Quand une campagne trouvera des profils pour vos besoins, on vous en proposera un paquet — une notification vous préviendra.</div>
      )}

      {courant && (
        <div className="pp-scene">
          <div>
            {carte ? (
              <div className="pp-pile">
                {restantes > 2 && <div className="pp-tranche" style={{ top: 0, left: 24, right: 24 }} aria-hidden="true" />}
                {restantes > 1 && <div className="pp-tranche" style={{ top: 6 }} aria-hidden="true" />}
                <div className={`pp-carte${drag.actif ? ' glisse' : ' libre'}`} style={{ transform }}
                  onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}
                  role="group" aria-roledescription="carte de profil" aria-label={`${c.name || 'Profil'} — ${restantes} à trier`}>
                  {dx > 8 && <div className="pp-bord pp-bord--oui" style={{ opacity: Math.min(1, dx / SEUIL_X) }} aria-hidden="true" />}
                  {dx < -8 && <div className="pp-bord pp-bord--non" style={{ opacity: Math.min(1, -dx / SEUIL_X) }} aria-hidden="true" />}
                  {dx > SEUIL_X * 0.6 && <span className="pp-sceau pp-sceau--oui" aria-hidden="true">Intéressant</span>}
                  {dx < -SEUIL_X * 0.6 && <span className="pp-sceau pp-sceau--non" aria-hidden="true">Pas intéressant</span>}
                  <div className="pp-haut">
                    <div className="pp-tete-g">
                      <div className="pp-score">{c.score != null ? Math.round(Number(c.score)) : '—'}<small>score</small></div>
                      <AvatarCandidat cand={nomEnDeux(c)} url={carte.photo_preview_url || null}
                        size="44px" radius="8px" fontSize="15px" className="pp-visage" />
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <p className="pp-nom">{c.name || 'Profil sans nom'}</p>
                      {(c.titre || c.entreprise) && <div className="pp-poste">{[c.titre, c.entreprise].filter(Boolean).join(' · ')}</div>}
                      {c.lieu && <div className="pp-lieu"><BI icon="geo-alt" style={{ fontSize: '10px' }} /> {c.lieu}</div>}
                    </div>
                  </div>
                  {puces.length > 0 && (
                    <div className="pp-puces">{puces.map((p, i) => <span key={i} className={`pp-puce${p.k ? ` ${p.k}` : ''}`}><BI icon={p.i} style={{ fontSize: '9px' }} /> {p.t}</span>)}</div>
                  )}
                  {(c.forts?.length > 0 || c.faibles?.length > 0) && (
                    <div className="pp-cols">
                      <div>
                        <div className="pp-lbl">Pour</div>
                        {(c.forts || []).map((f, i) => <div key={i} className="pp-li"><BI icon="check2" style={{ color: 'var(--erp-success)', flex: 'none' }} /> {f}</div>)}
                        {!c.forts?.length && <div className="pp-li" style={{ color: 'var(--erp-text-tertiary)' }}>—</div>}
                      </div>
                      <div>
                        <div className="pp-lbl">Contre</div>
                        {(c.faibles || []).map((f, i) => <div key={i} className="pp-li"><BI icon="dash" style={{ color: 'var(--erp-text-tertiary)', flex: 'none' }} /> {f}</div>)}
                        {!c.faibles?.length && <div className="pp-li" style={{ color: 'var(--erp-text-tertiary)' }}>—</div>}
                      </div>
                    </div>
                  )}
                  {c.raisons && <div className="pp-raisons" title={c.raisons}>{c.raisons}</div>}
                  {carte.note && <div className="pp-note"><b>{carte.propose_par || 'Note'}</b> : {carte.note}</div>}
                  <div className="pp-pied">
                    <span>Proposé{carte.propose_par ? ` par ${carte.propose_par}` : ''}{carte.propose_le ? ` le ${fmtJour(carte.propose_le)}` : ''}{carte.reporte ? ' · remis à plus tard' : ''}</span>
                    {c.profile_url && <a href={c.profile_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}>Voir le profil <BI icon="box-arrow-up-right" style={{ fontSize: '9px' }} /></a>}
                  </div>
                  {motifsOuverts && (
                    <div className="pp-motifs" role="dialog" aria-label="Pourquoi pas intéressant ?">
                      <div className="pp-motifs-t"><span>Pourquoi pas ?</span><button type="button" className="pp-lien" onClick={() => { setMotifsOuverts(false); setDrag({ dx: 0, dy: 0, actif: false }); }}>Annuler <span className="pp-kbd">Échap</span></button></div>
                      <div className="pp-motifs-l">
                        {motifs.map((m, i) => (
                          <button key={m.cle} type="button" className="erp-btn erp-btn--secondary erp-btn--sm" disabled={busy} autoFocus={i === 0} onClick={() => pasInteressant(m.cle)}>
                            {m.libelle}<span className="pp-kbd">{i + 1}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="pp-actions">
                  <button type="button" className="erp-btn erp-btn--secondary pp-btn" disabled={busy || motifsOuverts} onClick={() => setMotifsOuverts(true)}>
                    <BI icon="x-lg" /> Pas intéressant<span className="pp-kbd">←</span>
                  </button>
                  <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm pp-btn" disabled={busy || motifsOuverts || restantes < 2} onClick={plusTard} title="Remettre cette carte en fin de paquet">
                    Plus tard<span className="pp-kbd">↑</span>
                  </button>
                  <button type="button" className="erp-btn erp-btn--primary pp-btn" disabled={busy || motifsOuverts} onClick={interessant}>
                    <BI icon={busy ? 'arrow-repeat' : 'check2'} style={busy ? { display: 'inline-block', animation: 'ats-spin .8s linear infinite' } : undefined} /> Intéressant<span className="pp-kbd" style={{ color: 'inherit', borderColor: 'currentColor' }}>→</span>
                  </button>
                </div>
                <div className="pp-annuler">
                  <button type="button" className="pp-lien" disabled={busy || !historique.length} onClick={() => annulerDernier()}>Annuler le dernier geste</button>
                  <span className="pp-kbd">Z</span>
                  <span style={{ marginLeft: 10, color: 'var(--erp-text-tertiary)' }}>· {restantes} à trier sur « {courant.besoin.titre} » · glissez la carte, ou utilisez les flèches</span>
                </div>
              </div>
            ) : (
              <div className="pp-vide">
                Tout est trié sur « {courant.besoin.titre} » — {courant.faits.interessant || 0} intéressant{(courant.faits.interessant || 0) > 1 ? 's' : ''}, {courant.faits.pas_interessant || 0} non.
                {typeof onOuvrirBesoin === 'function' && <> <button type="button" className="pp-lien" onClick={() => onOuvrirBesoin(courant.besoin.id)}>Ouvrir le besoin</button> pour qualifier ceux que vous avez pris.</>}
              </div>
            )}
          </div>
          <aside className="pp-cote" aria-label="Déjà triés">
            <h3>Déjà triés</h3>
            {(courant.derniers || []).length === 0 && <div>Rien pour l’instant.</div>}
            {(courant.derniers || []).map(d => (
              <div key={d.id} className="pp-fait">
                <BI icon={d.decision === 'interessant' ? 'check2-circle' : 'x-circle'} style={{ color: d.decision === 'interessant' ? 'var(--erp-success)' : 'var(--erp-text-tertiary)', flex: 'none' }} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  {d.decision === 'interessant' && d.candidate_id != null && typeof onOuvrirCandidat === 'function'
                    ? <button type="button" className="pp-lien" style={{ color: 'var(--erp-ink-strong)', textDecoration: 'none', fontWeight: 600 }} onClick={() => onOuvrirCandidat(d.candidate_id)}>{d.carte?.name || 'Profil'}</button>
                    : <span style={{ fontWeight: 600, color: 'var(--erp-ink-strong)' }}>{d.carte?.name || 'Profil'}</span>}
                  <span style={{ color: 'var(--erp-text-tertiary)' }}> · {d.decision === 'interessant' ? 'dans le besoin' : (d.motif_libelle || 'pas intéressant')}</span>
                </span>
                <button type="button" className="pp-lien" disabled={busy} onClick={() => annulerDernier(d.id)} title={d.decision === 'interessant' ? 'Le profil ressort du besoin et revient à trier' : 'La carte revient à trier'}>annuler</button>
              </div>
            ))}
            {typeof onOuvrirBesoin === 'function' && (
              <div style={{ marginTop: 12 }}><button type="button" className="pp-lien" onClick={() => onOuvrirBesoin(courant.besoin.id)}>Voir le besoin « {courant.besoin.titre} »</button></div>
            )}
          </aside>
        </div>
      )}
    </div>
  );
}
